import { useState } from 'react';
import { SECTION_IDS, SIGN_IN_URL } from '../lib/links';
import demoPoster from '../assets/v2-demo-poster.png';
import arrowDown from '../assets/v2-arrow-down.svg';

const DEMO_YOUTUBE_ID = 'MhWbqjYiui8';
const demoEmbedUrl =
  `https://www.youtube-nocookie.com/embed/${DEMO_YOUTUBE_ID}` +
  '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

/**
 * "See WisdomX ORBIS in Action" — Figma node 2:443.
 *
 * Design values (at the 1920 reference width):
 *  · band    #141B34, px-133 py-80, flex-col items-center, gap-36
 *  · heading Inter 700 48/48, centred, white, w-875, "ORBIS" in #E87722
 *  · copy    Inter 400 18/24, centred, white, "ORBIS" bold #E87722
 *  · frame   p-14, r-16, fill rgba(255,255,255,0.18),
 *            inset ring rgba(255,255,255,0.46)
 *  · well    877×363 in Figma; built at the dashboard image's 1672×941 instead
 *            — see the note on the well below
 *  · CTAs    gap-32; "Watch Demo" #E87722 px-30 py-17 r-8 w-235.757 Inter 700 18px;
 *            "Request a live Demo" Inter 500 18px white + 24px arrow, gap-14
 *
 * The design shows the thumbnail with NO play overlay — the "Watch Demo" button
 * is the affordance — so none is added here. Pressing it swaps the poster for a
 * YouTube iframe (video id MhWbqjYiui8, youtube-nocookie for privacy), which is
 * why the embed and its ~500 KB of player JS never load on an idle page view.
 *
 * The poster is the dashboard image's RAW source (1672×941), not an export of
 * Figma's 877×363 well node. That node is a narrow strip of the same image, and
 * exporting it baked a chrome band into one edge — which is what made the frame
 * look unequal. The raw is clean, shows the whole dashboard, and already contains
 * the play graphic.
 *
 * Do not switch either media to `object-contain`. It keeps the frame whole but
 * letterboxes, and a bar in a different colour reads as a second container inside
 * the designed one. Filling is what keeps the border even.
 */
export function DemoVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id={SECTION_IDS.demo}
      className="w-full scroll-mt-16 bg-landing-demo-band px-5 py-12 sm:px-6 md:px-8 lg:scroll-mt-24 lg:px-10 lg:py-20 xl:px-16 2xl:px-[133px]"
    >
      <div className="mx-auto flex max-w-[905px] flex-col items-center gap-8 lg:gap-9">
        <div className="flex w-full flex-col gap-5 text-center lg:gap-[26px]">
          <h2 className="m-0 text-[28px] font-bold leading-[1.15] text-white sm:text-[34px] lg:text-[40px] 2xl:text-5xl 2xl:leading-[48px]">
            See WisdomX <span className="text-primary-500">ORBIS</span> in Action
          </h2>
          <p className="m-0 text-base leading-6 text-white lg:text-lg">
            A 3-minute overview of the WisdomX{' '}
            <strong className="font-bold text-primary-500">ORBIS</strong> platform - from estate
            discovery to governed decommission decision.
          </p>
        </div>

        {/* Framed well — white 18% fill with a 46% inset hairline. */}
        <div className="relative w-full overflow-hidden rounded-2xl bg-white/[0.18] p-2.5 lg:p-3.5">
          {/* Well aspect = the dashboard image's own 1672×941. Figma's well is
              877×363, which is a narrow strip of that image and left an uneven
              band along one edge; this fills edge to edge instead, so the only
              visible frame is the designed 14px padding — equal on all four
              sides, one colour. It is also taller, as asked.

              Both states use object-cover so neither can letterbox: a bar of a
              different colour is what made this read as two containers. */}
          <div className="relative aspect-[1672/941] w-full overflow-hidden rounded-md shadow-[0px_0px_0px_1px_rgba(17,24,39,0.1),0px_25px_50px_-12px_rgba(0,0,0,0.25)]">
            {playing ? (
              <iframe
                src={demoEmbedUrl}
                title="WisdomX ORBIS product demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="block h-full w-full border-0"
              />
            ) : (
              /* The poster has a play graphic baked into the Figma image, so it
                 reads as clickable. The design's stated affordance is the
                 "Watch Demo" button below, but leaving the obvious target dead
                 would be worse — this button is transparent and adds no visual
                 change, it just makes the graphic do what it looks like. */
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label="Play demo video"
                className="block h-full w-full cursor-pointer border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <img
                  src={demoPoster}
                  alt="WisdomX Orbis demo — Orbis Decommission Dashboard"
                  loading="lazy"
                  decoding="async"
                  width={1672}
                  height={941}
                  className="block h-full w-full object-cover object-center"
                />
              </button>
            )}
          </div>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0px_0px_0px_1px_rgba(255,255,255,0.46)]"
          />
        </div>

        <div className="flex w-full flex-col items-stretch gap-5 sm:w-auto sm:flex-row sm:items-center sm:gap-8">
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="flex cursor-pointer items-center justify-center rounded-lg border-0 bg-primary-500 px-[30px] py-[17px] text-lg font-bold text-white transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:w-[235.757px]"
          >
            Watch Demo
          </button>
          {/* NOTE: Figma gives this no destination. Production points it at SSO
              sign-in, unlike every other "Request a Demo" which opens the demo
              mailto — kept faithful to production, flagged for design review. */}
          <a
            href={SIGN_IN_URL}
            className="flex items-center justify-center gap-3.5 text-lg font-medium text-white no-underline transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            Request a live Demo
            <img
              src={arrowDown}
              alt=""
              aria-hidden="true"
              width={24}
              height={24}
              className="size-6 -rotate-90"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
